// Series primitives that draw the curated Model Book's hand-drawn marks
// (docs/features/CURATED_MODEL_BOOK.md).
//
// TWO primitives, because a mark lives in one of two coordinate spaces:
//   - price-pane kinds (base, contraction, level, breakout, note) attach to
//     the PRICE series and resolve `price` with its priceToCoordinate;
//   - volume kinds (volume_note, volume_arrow) attach to the VOLUME histogram
//     (the chart controller's `volumeSeries`) and resolve the bar's own
//     volume on that series' scale — the mark stores a date only.
//
// Marks are DATA-SPACE ({date, price}); x always comes from
// timeScale().timeToCoordinate(date) (never logicalToCoordinate, which
// silently returns 0 for fractional logicals), y from the owning series. A
// mark whose coordinates come back null — a date that is not a bar, or off
// the loaded window — is skipped, never drawn at the edge.
//
// Payload shape (chart_data's top-level `marks:`):
//   { id, kind, points: [{date, price}], label, note, author: {id, alias}, own }
//
// `ChartMarksLayer` owns both primitives plus the hover tooltip, and is what
// the chart controller's marks API (setMarks/addMark/removeMark/
// setVisibleAuthors) talks to. For the drawing editor
// (chart_marks_editor_controller.js) it adds `hitTest(x, y)` (Select tool),
// `setPreview(mark|null)` (one transient mark, same renderer, faded) and
// `setSelected(id|null)` (a dashed outline round one mark).

import { drawLabel, readTheme } from "./vcp_markup_overlay"
import { markStyle, MARK_FONT } from "./chart_mark_styles"

export const PRICE_KINDS = Object.freeze([ "base", "contraction", "level", "breakout", "note" ])
export const VOLUME_KINDS = Object.freeze([ "volume_note", "volume_arrow" ])

const HIT_TOLERANCE_PX = 8
const ARROW_LENGTH = 16
const ARROW_HEAD = 6
const ARROW_GAP = 4
const RING_MIN_RADIUS = 7
const NOTE_OFFSET_X = 22
const NOTE_OFFSET_Y = -26
const NOTE_PAD = 4
// The drawing editor's live preview: the same renderer, faded.
export const PREVIEW_ALPHA = 0.5
const SELECTION_PAD = 5

const authorKey = id => (id === null || id === undefined ? "" : String(id))

// The marks worth drawing on one pane, normalised: a known kind, ISO dates,
// numeric prices where the pane needs them. Pure, so it is testable without a
// canvas.
export function normaliseMarks(marks, pane = "price") {
  if (!Array.isArray(marks)) return []
  const kinds = pane === "volume" ? VOLUME_KINDS : PRICE_KINDS

  return marks.reduce((out, mark) => {
    if (!mark || !kinds.includes(mark.kind) || !Array.isArray(mark.points) || mark.points.length === 0) return out
    const points = mark.points.map(point => ({
      time: point && point.date ? String(point.date) : null,
      price: point && point.price !== null && point.price !== undefined ? Number(point.price) : null
    }))
    if (points.some(point => !point.time)) return out
    if (pane === "price" && points.some(point => !(point.price > 0))) return out

    out.push({
      id: mark.id,
      kind: mark.kind,
      points,
      label: mark.label ? String(mark.label) : "",
      note: mark.note ? String(mark.note) : "",
      authorId: authorKey(mark.author?.id),
      authorAlias: mark.author?.alias ? String(mark.author.alias) : "",
      own: !!mark.own
    })
    return out
  }, [])
}

// The visible subset: `visible` is a Set of author keys, or null for all.
export function visibleMarks(marks, visible) {
  if (!visible) return marks
  return marks.filter(mark => visible.has(mark.authorId))
}

// Price extremes the price-pane marks need on screen (a level above the bars
// must not be autoscaled off the top). Null when nothing carries a price.
export function markPriceRange(marks) {
  const prices = marks.flatMap(mark => mark.points.map(point => point.price)).filter(price => price > 0)
  if (prices.length === 0) return null
  return { minValue: Math.min(...prices), maxValue: Math.max(...prices) }
}

export function distanceToSegment(px, py, x0, y0, x1, y1) {
  const dx = x1 - x0
  const dy = y1 - y0
  const lengthSq = dx * dx + dy * dy
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - x0) * dx + (py - y0) * dy) / lengthSq))
  return Math.hypot(px - (x0 + t * dx), py - (y0 + t * dy))
}

// Screen geometry for one mark, or null when it cannot be placed. `volumeAt`
// resolves a date to that bar's volume (volume pane only).
export function markGeometry(mark, { timeToX, priceToY, volumeAt, width }) {
  const xs = mark.points.map(point => timeToX(point.time))
  if (xs.some(x => x === null || x === undefined)) return null

  if (mark.kind === "volume_note" || mark.kind === "volume_arrow") {
    const volume = volumeAt(mark.points[0].time)
    if (volume === null || volume === undefined) return null
    const y = priceToY(volume)
    if (y === null || y === undefined) return null
    return { kind: mark.kind, x: xs[0], y }
  }

  const ys = mark.points.map(point => priceToY(point.price))
  if (ys.some(y => y === null || y === undefined)) return null

  switch (mark.kind) {
  case "base":
    return {
      kind: "base",
      left: Math.min(xs[0], xs[1]), right: Math.max(xs[0], xs[1]),
      top: Math.min(ys[0], ys[1]), bottom: Math.max(ys[0], ys[1])
    }
  case "contraction":
    return { kind: "contraction", x0: xs[0], y0: ys[0], x1: xs[1], y1: ys[1] }
  case "level":
    // One point: across the whole pane. Two: across that span, at the
    // FIRST point's price (ChartMark::KINDS).
    return xs.length > 1
      ? { kind: "level", x0: Math.min(xs[0], xs[1]), x1: Math.max(xs[0], xs[1]), y: ys[0] }
      : { kind: "level", x0: 0, x1: width, y: ys[0] }
  case "breakout":
  case "note":
    return { kind: mark.kind, x: xs[0], y: ys[0] }
  default:
    return null
  }
}

// Pixel distance from (px, py) to a mark's geometry — 0 inside a base.
export function geometryDistance(geometry, px, py) {
  switch (geometry.kind) {
  case "base": {
    const dx = Math.max(geometry.left - px, 0, px - geometry.right)
    const dy = Math.max(geometry.top - py, 0, py - geometry.bottom)
    return Math.hypot(dx, dy)
  }
  case "contraction":
    return distanceToSegment(px, py, geometry.x0, geometry.y0, geometry.x1, geometry.y1)
  case "level":
    return distanceToSegment(px, py, geometry.x0, geometry.y, geometry.x1, geometry.y)
  case "breakout":
    return distanceToSegment(px, py, geometry.x, geometry.y + ARROW_GAP, geometry.x, geometry.y + ARROW_GAP + ARROW_LENGTH)
  case "note":
    return Math.min(Math.hypot(px - geometry.x, py - geometry.y),
      Math.hypot(px - (geometry.x + NOTE_OFFSET_X), py - (geometry.y + NOTE_OFFSET_Y)))
  case "volume_arrow":
    return distanceToSegment(px, py, geometry.x, geometry.y - ARROW_GAP - ARROW_LENGTH, geometry.x, geometry.y - ARROW_GAP)
  default:
    return Math.hypot(px - geometry.x, py - geometry.y)
  }
}

// The box the editor's selection outline is drawn around, padded; `radius`
// is a volume ring's radius. Pure.
export function geometryBounds(geometry, { radius = RING_MIN_RADIUS } = {}) {
  const pad = SELECTION_PAD
  switch (geometry.kind) {
  case "base":
    return { left: geometry.left - pad, top: geometry.top - pad, right: geometry.right + pad, bottom: geometry.bottom + pad }
  case "contraction":
    return {
      left: Math.min(geometry.x0, geometry.x1) - pad, right: Math.max(geometry.x0, geometry.x1) + pad,
      top: Math.min(geometry.y0, geometry.y1) - pad, bottom: Math.max(geometry.y0, geometry.y1) + pad
    }
  case "level":
    return { left: geometry.x0, right: geometry.x1, top: geometry.y - pad, bottom: geometry.y + pad }
  case "breakout":
    return { left: geometry.x - ARROW_HEAD - pad, right: geometry.x + ARROW_HEAD + pad, top: geometry.y, bottom: geometry.y + ARROW_GAP + ARROW_LENGTH + pad }
  case "note":
    return {
      left: geometry.x - pad, right: geometry.x + NOTE_OFFSET_X + pad,
      top: geometry.y + NOTE_OFFSET_Y - pad, bottom: geometry.y + pad
    }
  case "volume_arrow":
    return { left: geometry.x - ARROW_HEAD - pad, right: geometry.x + ARROW_HEAD + pad, top: geometry.y - ARROW_GAP - ARROW_LENGTH - pad, bottom: geometry.y }
  default:
    return { left: geometry.x - radius - pad, right: geometry.x + radius + pad, top: geometry.y - radius - pad, bottom: geometry.y + radius + pad }
  }
}

function drawArrow(ctx, x, tipY, direction, style) {
  // direction -1: pointing up (tip on top); +1: pointing down.
  const tailY = tipY - direction * ARROW_LENGTH
  ctx.strokeStyle = style.stroke
  ctx.fillStyle = style.fill || style.stroke
  ctx.lineWidth = style.lineWidth
  ctx.setLineDash(style.dash)
  ctx.beginPath()
  ctx.moveTo(x, tailY)
  ctx.lineTo(x, tipY - direction * ARROW_HEAD)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(x, tipY)
  ctx.lineTo(x - ARROW_HEAD / 1.4, tipY - direction * ARROW_HEAD)
  ctx.lineTo(x + ARROW_HEAD / 1.4, tipY - direction * ARROW_HEAD)
  ctx.closePath()
  ctx.fill()
}

class MarksRenderer {
  constructor(source) { this._source = source }

  draw(target) {
    const source = this._source
    if (!source.chart || !source.series) return
    const marks = source.drawableMarks()
    const preview = source.preview
    if (marks.length === 0 && !preview) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const { width } = scope.mediaSize
      const theme = source.theme
      const resolve = source.resolver(width)

      marks.forEach(mark => {
        const geometry = markGeometry(mark, resolve)
        const style = markStyle(mark.kind, theme.dark)
        if (!geometry || !style) return
        ctx.save()
        this._draw(ctx, geometry, mark, style, theme, width)
        ctx.restore()
        if (source.selectedId !== null && mark.id === source.selectedId) {
          ctx.save()
          this._drawSelection(ctx, geometry, style)
          ctx.restore()
        }
      })

      // The editor's placement preview: one transient mark, this renderer,
      // faded — never a second drawing path.
      if (preview) {
        const geometry = markGeometry(preview, resolve)
        const style = markStyle(preview.kind, theme.dark)
        if (!geometry || !style) return
        ctx.save()
        ctx.globalAlpha = PREVIEW_ALPHA
        this._draw(ctx, geometry, preview, style, theme, width)
        ctx.restore()
      }
    })
  }

  _drawSelection(ctx, geometry, style) {
    const radius = Math.max(RING_MIN_RADIUS, this._source.barSpacing() * 0.8)
    const box = geometryBounds(geometry, { radius })
    ctx.strokeStyle = style.stroke
    ctx.lineWidth = 1
    ctx.setLineDash([ 3, 3 ])
    ctx.strokeRect(box.left, box.top, box.right - box.left, box.bottom - box.top)
  }

  _draw(ctx, g, mark, style, theme, width) {
    const bg = theme.background
    ctx.lineWidth = style.lineWidth
    ctx.setLineDash(style.dash)
    ctx.strokeStyle = style.stroke

    switch (g.kind) {
    case "base":
      ctx.fillStyle = style.fill
      ctx.fillRect(g.left, g.top, g.right - g.left, g.bottom - g.top)
      ctx.strokeRect(g.left, g.top, g.right - g.left, g.bottom - g.top)
      if (mark.label) drawLabel(ctx, mark.label, g.left + 4, g.top + 9, style.stroke, bg, MARK_FONT, "left")
      break
    case "contraction":
      ctx.beginPath()
      ctx.moveTo(g.x0, g.y0)
      ctx.lineTo(g.x1, g.y1)
      ctx.stroke()
      ctx.setLineDash([])
      if (mark.label) drawLabel(ctx, mark.label, (g.x0 + g.x1) / 2, Math.min(g.y0, g.y1) - 9, style.stroke, bg, MARK_FONT, "center")
      break
    case "level":
      ctx.beginPath()
      ctx.moveTo(g.x0, g.y)
      ctx.lineTo(g.x1, g.y)
      ctx.stroke()
      if (mark.label) drawLabel(ctx, mark.label, Math.min(g.x1, width - 4), g.y - 8, style.stroke, bg, MARK_FONT, "right")
      break
    case "breakout":
      // Under the bar, pointing up at the marked price.
      drawArrow(ctx, g.x, g.y + ARROW_GAP, -1, style)
      if (mark.label) drawLabel(ctx, mark.label, g.x, g.y + ARROW_GAP + ARROW_LENGTH + 8, style.stroke, bg, MARK_FONT, "center")
      break
    case "note": {
      const text = mark.label || mark.note.split("\n")[0].slice(0, 40)
      const bx = g.x + NOTE_OFFSET_X
      const by = g.y + NOTE_OFFSET_Y
      ctx.beginPath()
      ctx.moveTo(g.x, g.y)
      ctx.lineTo(bx, by)
      ctx.stroke()
      ctx.fillStyle = style.stroke
      ctx.beginPath()
      ctx.arc(g.x, g.y, 2.5, 0, Math.PI * 2)
      ctx.fill()
      if (text) {
        ctx.font = MARK_FONT
        const w = ctx.measureText(text).width
        const left = Math.min(bx, width - w - 2 * NOTE_PAD - 2)
        ctx.fillStyle = bg
        ctx.fillRect(left, by - 8, w + 2 * NOTE_PAD, 16)
        ctx.strokeRect(left, by - 8, w + 2 * NOTE_PAD, 16)
        drawLabel(ctx, text, left + NOTE_PAD, by, style.text || style.stroke, bg, MARK_FONT, "left")
      }
      break
    }
    case "volume_note": {
      const radius = Math.max(RING_MIN_RADIUS, this._source.barSpacing() * 0.8)
      ctx.beginPath()
      ctx.arc(g.x, g.y, radius, 0, Math.PI * 2)
      ctx.stroke()
      if (mark.label) drawLabel(ctx, mark.label, g.x, g.y - radius - 8, style.stroke, bg, MARK_FONT, "center")
      break
    }
    case "volume_arrow":
      // Above the volume bar, pointing down at its top.
      drawArrow(ctx, g.x, g.y - ARROW_GAP, 1, style)
      if (mark.label) drawLabel(ctx, mark.label, g.x, g.y - ARROW_GAP - ARROW_LENGTH - 8, style.stroke, bg, MARK_FONT, "center")
      break
    }
  }
}

class MarksPaneView {
  constructor(source) { this._source = source }
  update() {}
  renderer() { return new MarksRenderer(this._source) }
  zOrder() { return "top" }
}

// One pane's primitive. Attach the "price" one to the price series and the
// "volume" one to the volume histogram.
export class ChartMarksOverlay {
  constructor({ pane = "price", marks = [], visibleAuthorIds = null } = {}) {
    this.pane = pane
    this.chart = null
    this.series = null
    this.theme = readTheme()
    this._marks = normaliseMarks(marks, pane)
    this._visible = visibleAuthorIds
    this.preview = null
    this.selectedId = null
    this._volumeByTime = null
    this._volumeDataLength = -1
    this._paneViews = [ new MarksPaneView(this) ]
    this._requestUpdate = null
    this._onThemeChanged = () => {
      this.theme = readTheme()
      this.requestRedraw()
    }
  }

  setMarks(marks) {
    this._marks = normaliseMarks(marks, this.pane)
    this.requestRedraw()
  }

  // @param visible a Set of author keys (String(author.id), "" for none), or null for all
  setVisibleAuthors(visible) {
    this._visible = visible
    this.requestRedraw()
  }

  drawableMarks() {
    return visibleMarks(this._marks, this._visible)
  }

  // One transient mark (the editor's placement preview), or null. Drawn
  // whatever the author filter, never hit-tested, never autoscaled.
  setPreview(mark) {
    const [ normalised ] = mark ? normaliseMarks([ mark ], this.pane) : []
    this.preview = normalised || null
    this.requestRedraw()
  }

  // The id of the mark to outline as selected, or null.
  setSelected(id) {
    this.selectedId = id === undefined ? null : id
    this.requestRedraw()
  }

  requestRedraw() {
    if (this._requestUpdate) this._requestUpdate()
  }

  barSpacing() {
    try { return this.chart.timeScale().options().barSpacing || 6 } catch { return 6 }
  }

  volumeAt(time) {
    if (!this.series || typeof this.series.data !== "function") return null
    const data = this.series.data()
    if (!this._volumeByTime || this._volumeDataLength !== data.length) {
      this._volumeByTime = new Map(data.map(bar => [ String(bar.time), bar.value ]))
      this._volumeDataLength = data.length
    }
    const value = this._volumeByTime.get(String(time))
    return value > 0 ? value : null
  }

  resolver(width) {
    const timeScale = this.chart.timeScale()
    return {
      timeToX: time => timeScale.timeToCoordinate(time),
      priceToY: price => this.series.priceToCoordinate(price),
      volumeAt: time => this.volumeAt(time),
      width
    }
  }

  // The nearest visible mark within the tolerance of (x, y) — pane media
  // coordinates — as { mark, distance }, or null.
  hitTest(x, y, tolerance = HIT_TOLERANCE_PX) {
    if (!this.chart || !this.series) return null
    let width = 0
    try { width = this.chart.timeScale().width() } catch { /* no-op */ }
    const resolve = this.resolver(width)
    let best = null
    this.drawableMarks().forEach(mark => {
      const geometry = markGeometry(mark, resolve)
      if (!geometry) return
      const distance = geometryDistance(geometry, x, y)
      if (distance <= tolerance && (!best || distance < best.distance)) best = { mark, distance }
    })
    return best
  }

  attached(param) {
    this.chart = param.chart
    this.series = param.series
    this._requestUpdate = param.requestUpdate
    this.theme = readTheme()
    document.addEventListener("theme:changed", this._onThemeChanged)
    param.requestUpdate()
  }

  detached() {
    document.removeEventListener("theme:changed", this._onThemeChanged)
    this._requestUpdate = null
    this.chart = null
    this.series = null
  }

  updateAllViews() {}

  paneViews() {
    return this._paneViews
  }

  // Keep the marked prices of the marks in view on screen; the volume pane
  // keeps its own scale. `startLogical`/`endLogical` are the visible logical
  // range the library passes in: only a mark whose bar span overlaps it
  // counts, so zooming into a late breakout on a 10x stock does not keep the
  // early base's prices in scale and flatten the candles.
  autoscaleInfo(startLogical, endLogical) {
    if (this.pane !== "price") return null
    const range = markPriceRange(this.marksInLogicalRange(this.drawableMarks(), startLogical, endLogical))
    return range ? { priceRange: range } : null
  }

  // The marks whose [first, last] bar index overlaps [start, end]. Without a
  // range or a time scale that can map dates to indices, every mark counts.
  marksInLogicalRange(marks, start, end) {
    if (!Number.isFinite(start) || !Number.isFinite(end)) return marks
    let timeScale = null
    try { timeScale = this.chart?.timeScale() } catch { /* no-op */ }
    if (!timeScale || typeof timeScale.timeToIndex !== "function") return marks
    return marks.filter(mark => {
      const indices = mark.points
        .map(point => { try { return timeScale.timeToIndex(point.time, true) } catch { return null } })
        .filter(index => index !== null && index !== undefined && Number.isFinite(index))
      if (indices.length === 0) return false
      return Math.max(...indices) >= start && Math.min(...indices) <= end
    })
  }
}

// Both primitives, the raw mark list and the hover tooltip, for one chart.
export class ChartMarksLayer {
  constructor({ chart, priceSeries, volumeSeries, container }) {
    this.chart = chart
    this.marks = []
    this.visible = null
    this.selectedId = null
    this.price = new ChartMarksOverlay({ pane: "price" })
    this.volume = volumeSeries ? new ChartMarksOverlay({ pane: "volume" }) : null
    priceSeries.attachPrimitive(this.price)
    if (this.volume) volumeSeries.attachPrimitive(this.volume)
    this._priceSeries = priceSeries
    this._volumeSeries = volumeSeries
    this._container = container
    this._tooltip = null
    this._onCrosshairMove = param => this._hover(param)
    chart.subscribeCrosshairMove(this._onCrosshairMove)
  }

  setMarks(marks) {
    this.marks = Array.isArray(marks) ? marks.slice() : []
    this._sync()
  }

  addMark(mark) {
    if (!mark) return
    this.marks = this.marks.filter(existing => existing.id !== mark.id).concat([ mark ])
    this._sync()
  }

  removeMark(id) {
    this.marks = this.marks.filter(mark => mark.id !== id)
    if (this.selectedId === id) this.setSelected(null)
    this._sync()
  }

  // The drawing editor's live preview: one transient mark drawn by the same
  // renderer at reduced opacity, on whichever pane its kind belongs to; null
  // clears it.
  setPreview(mark) {
    const onVolume = !!mark && VOLUME_KINDS.includes(mark.kind)
    this.price.setPreview(mark && !onVolume ? mark : null)
    if (this.volume) this.volume.setPreview(onVolume ? mark : null)
  }

  // Outline one mark as the editor's selection; null clears it.
  setSelected(id) {
    this.selectedId = id === undefined ? null : id
    this.price.setSelected(this.selectedId)
    if (this.volume) this.volume.setSelected(this.selectedId)
  }

  // @param ids author ids to show, or null/undefined to show everyone
  setVisibleAuthors(ids) {
    this.visible = ids === null || ids === undefined ? null : new Set(Array.from(ids, authorKey))
    this.price.setVisibleAuthors(this.visible)
    if (this.volume) this.volume.setVisibleAuthors(this.visible)
    // A hidden mark can't stay selected (Delete would remove what you can't see).
    if (this.selectedId !== null && !this.isMarkVisible(this.selectedId)) this.setSelected(null)
  }

  // Whether the mark with `id` is held and its author is shown.
  isMarkVisible(id) {
    const mark = this.marks.find(existing => existing.id === id)
    if (!mark) return false
    return !this.visible || this.visible.has(authorKey(mark.author?.id))
  }

  // The nearest visible mark to (x, y) in pane-0 coordinates, either pane.
  hitTest(x, y) {
    const hits = [ this.price.hitTest(x, y), this.volume?.hitTest(x, y) ].filter(Boolean)
    if (hits.length === 0) return null
    hits.sort((a, b) => a.distance - b.distance)
    return this.marks.find(mark => mark.id === hits[0].mark.id) || null
  }

  dispose() {
    try { this.chart.unsubscribeCrosshairMove(this._onCrosshairMove) } catch { /* no-op */ }
    try { this._priceSeries.detachPrimitive(this.price) } catch { /* no-op */ }
    if (this.volume) {
      try { this._volumeSeries.detachPrimitive(this.volume) } catch { /* no-op */ }
    }
    // detachPrimitive runs detached(); repeat it in case the detach threw, so
    // the theme:changed listener can never outlive the chart (idempotent).
    this.price.detached()
    this.volume?.detached()
    this._tooltip?.remove()
    this._tooltip = null
  }

  _sync() {
    this.price.setMarks(this.marks)
    if (this.volume) this.volume.setMarks(this.marks)
  }

  _hover(param) {
    const point = param?.point
    const onMainPane = param?.paneIndex === undefined || param.paneIndex === 0
    const mark = point && onMainPane ? this.hitTest(point.x, point.y) : null
    if (!mark) {
      if (this._tooltip) this._tooltip.style.display = "none"
      return
    }
    const tooltip = this._ensureTooltip()
    if (!tooltip) return
    tooltip.replaceChildren()
    const author = document.createElement("div")
    author.className = "fw-semibold"
    author.textContent = mark.author?.alias || ""
    tooltip.appendChild(author)
    if (mark.label) {
      const label = document.createElement("div")
      label.textContent = mark.label
      tooltip.appendChild(label)
    }
    if (mark.note) {
      const note = document.createElement("div")
      note.className = "text-body-secondary"
      note.style.whiteSpace = "pre-wrap"
      note.textContent = mark.note
      tooltip.appendChild(note)
    }
    tooltip.style.left = `${point.x + 14}px`
    tooltip.style.top = `${point.y + 14}px`
    tooltip.style.display = "block"
  }

  _ensureTooltip() {
    if (this._tooltip && this._tooltip.isConnected) return this._tooltip
    if (!this._container) return null
    const tooltip = document.createElement("div")
    tooltip.className = "chart-mark-tooltip card shadow-sm small p-2"
    Object.assign(tooltip.style, {
      position: "absolute", zIndex: "5", pointerEvents: "none", maxWidth: "18rem", display: "none"
    })
    this._container.appendChild(tooltip)
    this._tooltip = tooltip
    return tooltip
  }
}
